import { useState } from 'react';
import { useMeetingDetail } from '../../../queries/meetings';
import { useMeetingRaces, useRacesStatus, useRequeueRaceForm } from '../../../queries/meetingRaces';
import { formatDateLocal } from '../meetings-helpers';
import { ModelRunControl, ModelSelection } from '../../../components/ModelRunControl';
import { RacesTable } from './RacesTable';
import { AnalysisActionsBar } from './AnalysisActionsBar';
import { MeetingActionsMenu } from './MeetingActionsMenu';
import { RaceMatrixView } from './RaceMatrixView';
import { HistoricalPerformanceView } from './HistoricalPerformanceView';
import { AutoReviewPanel } from './AutoReviewPanel';
import { Card, CardContent } from '../../../components/ui/Card';
import { Spinner } from '../../../components/ui/Spinner';
import { cn } from '../../../lib/utils';
import { extractErrorMessage } from '../../../api/errors';
import { useToast } from '../../../contexts/ToastContext';

const TABS = [
  { key: 'races', label: 'Races' },
  { key: 'matrix', label: 'Matrix' },
  { key: 'auto-review', label: 'Auto review' },
  { key: 'history', label: 'History' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * The meeting drill-down body — analysis actions, per-race run/swap, matrix and
 * history — shared by the full-page route and the slide-over opened from a
 * meeting card. Tab state is internal so it works identically in both.
 */
export function RaceDrillDownContent({ meetingId }: { meetingId: string }) {
  const [activeTab, setActiveTab] = useState<TabKey>('races');
  const { data: meeting } = useMeetingDetail(meetingId);
  const { data: races, isLoading: racesLoading, error } = useMeetingRaces(meetingId);
  const { data: raceStatus = new Map() } = useRacesStatus(meetingId);
  const requeue = useRequeueRaceForm(meetingId);
  const { success, error: toastError } = useToast();
  const [requeuingRaceId, setRequeuingRaceId] = useState<string | null>(null);

  // Shared model+slot selection set by the parent "run all" control; every race
  // inherits it unless individually overridden. Changing the parent resets overrides.
  const [sharedSelection, setSharedSelection] = useState<ModelSelection>({ modelId: null, slot: 1 });
  const [raceOverrides, setRaceOverrides] = useState<Record<number, ModelSelection>>({});
  const setShared = (modelId: number, slot: number) => {
    setSharedSelection({ modelId, slot });
    setRaceOverrides({});
  };
  const setOverride = (raceNumber: number, modelId: number, slot: number) =>
    setRaceOverrides((o) => ({ ...o, [raceNumber]: { modelId, slot } }));

  const handleRequeue = (raceId: string) => {
    setRequeuingRaceId(raceId);
    requeue.mutate(raceId, {
      onSuccess: () => success('Race form re-download queued.'),
      onError: () => toastError('Failed to queue the form re-download. Please try again.'),
      onSettled: () => setRequeuingRaceId(null),
    });
  };

  const meetingDate = meeting?.meeting_date ? formatDateLocal(new Date(meeting.meeting_date)) : '';

  return (
    <div className="space-y-5">
      {meeting && <AnalysisActionsBar meetingId={meetingId} />}

      <div className="flex items-center justify-between gap-2 border-b border-border">
        <div className="flex items-center gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {meeting && <MeetingActionsMenu meetingId={meetingId} />}
      </div>

      {activeTab === 'races' && meeting && (
        <Card>
          <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold">All races</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Set the model + slot for every race (each race follows this unless overridden below), or run them all
                at once.
              </p>
            </div>
            <ModelRunControl
              trackId={meeting.track_id}
              meetingDate={meetingDate}
              selection={sharedSelection}
              onSelectionChange={setShared}
              runVerbOverride="Run all"
            />
          </CardContent>
        </Card>
      )}

      {activeTab === 'races' &&
        (racesLoading ? (
          <Spinner label="Loading races…" />
        ) : error ? (
          <div className="rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground">
            {extractErrorMessage(error, 'Failed to load races.')}
          </div>
        ) : meeting ? (
          <RacesTable
            races={races ?? []}
            raceStatus={raceStatus}
            trackId={meeting.track_id}
            meetingDate={meetingDate}
            sharedSelection={sharedSelection}
            raceOverrides={raceOverrides}
            onOverride={setOverride}
            onRequeue={handleRequeue}
            requeuingRaceId={requeuingRaceId}
          />
        ) : null)}

      {activeTab === 'matrix' && <RaceMatrixView meetingId={meetingId} />}
      {activeTab === 'auto-review' && (
        <AutoReviewPanel meetingId={meetingId} trackId={meeting?.track_id} meetingDate={meetingDate} />
      )}
      {activeTab === 'history' && <HistoricalPerformanceView meetingId={meetingId} currentMeetingDate={meeting?.meeting_date} />}
    </div>
  );
}
